import { memo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { HStack } from "@design-system/HStack/HStack";
import { DraftAccount } from "../../draftState";
import {
    EditorRowCapabilities,
    GetRowCapabilities,
} from "../../editorCapabilities";
import { NestedEditorRow } from "../nestedRowTypes";
import { resolveRowAddScope } from "../nestedAddScope";
import {
    NestedAddScope,
    NestedEditorCallbacks,
    NestedEditorTableMeta,
} from "../nestedTableMeta";
import { NestedEditorHost } from "../nestedRowRefusal";
import {
    NestedAccountMenuItemState,
    resolveNestedAccountMenuItems,
} from "../nestedAccountMenuItems";
import { useNestedEditorIsWriting } from "../nestedEditorIsWritingContext";
import { NestedRefusalTooltip } from "./NestedRefusalTooltip";
import { isRowPending } from "../nestedPendingAdds";
import styles from "../ChartOfAccountsTemplateNestedEditor.module.scss";

interface EditorMenuItemProps {
    state: NestedAccountMenuItemState;
    label: string;
    onClick: (() => void) | undefined;
    // A single-business row has only one business the refusal could name, so the
    // reason alone says everything the list would.
    shouldNameBusinesses: boolean;
    isDestructive?: boolean;
}

const EditorMenuItem = ({
    state,
    label,
    onClick,
    shouldNameBusinesses,
    isDestructive = false,
}: EditorMenuItemProps) => {
    if (state.kind === "hidden" || !onClick) {
        return null;
    }
    if (state.kind === "disabled") {
        return (
            <Menu.Item
                destructive={isDestructive}
                disabled
                disabledReason={
                    state.refusalGroups ? (
                        <NestedRefusalTooltip
                            groups={state.refusalGroups}
                            shouldNameBusinesses={shouldNameBusinesses}
                        />
                    ) : (
                        state.tooltip
                    )
                }
            >
                {label}
            </Menu.Item>
        );
    }
    return (
        <Menu.Item
            destructive={isDestructive}
            onClick={onClick}
            tooltip={state.tooltip}
        >
            {label}
        </Menu.Item>
    );
};

// Per-row ⋯ menu for an account.
const AccountActionsMenu = ({
    row,
    account,
    capabilities,
    getConstituentCapabilities,
    callbacks,
    host,
    isWriting,
}: {
    row: NestedEditorRow;
    account: DraftAccount;
    capabilities: EditorRowCapabilities;
    getConstituentCapabilities: GetRowCapabilities;
    callbacks: NestedEditorCallbacks;
    host: NestedEditorHost;
    isWriting: boolean;
}) => {
    const archiveLabel = account.isDisabled ? "Enable" : "Archive";

    const menuItems = resolveNestedAccountMenuItems({
        row,
        host,
        isWriting,
        capabilities,
        isAccountArchived: account.isDisabled,
        getConstituentCapabilities,
    });
    const { onMergeAccount, onRemoveOpeningBalance, onReassignRole } =
        callbacks;
    const shouldNameBusinesses =
        new Set(row.accounts.map((constituent) => constituent.entityId)).size >
        1;

    return (
        <Menu.More size="md" className={viewTableStyles.showOnHover}>
            <EditorMenuItem
                shouldNameBusinesses={shouldNameBusinesses}
                state={menuItems.removeOpeningBalance}
                label="Remove opening balance"
                onClick={
                    onRemoveOpeningBalance
                        ? () => onRemoveOpeningBalance(account.id)
                        : undefined
                }
            />
            <EditorMenuItem
                shouldNameBusinesses={shouldNameBusinesses}
                state={menuItems.archive}
                label={archiveLabel}
                onClick={() =>
                    callbacks.onSetDisabled(account.id, !account.isDisabled)
                }
            />
            <EditorMenuItem
                shouldNameBusinesses={shouldNameBusinesses}
                state={menuItems.merge}
                label="Merge"
                onClick={
                    onMergeAccount
                        ? () => onMergeAccount(account.id)
                        : undefined
                }
            />
            <EditorMenuItem
                shouldNameBusinesses={shouldNameBusinesses}
                state={menuItems.reassignRole}
                label="Reassign role"
                onClick={() => onReassignRole(account.id)}
            />
            <EditorMenuItem
                shouldNameBusinesses={shouldNameBusinesses}
                state={menuItems.delete}
                label="Delete"
                onClick={() => callbacks.onDeleteAccount(account.id)}
                isDestructive
            />
        </Menu.More>
    );
};

// Hover-revealed "Add group" / "Add account" affordances clustered on the right
// of every row (before the ⋯ menu), each adding into the row's own scope. Lives
// in the actions column so all row kinds — including accounts and structural
// section / built-in tier rows — share one right cluster.
const RowAddActions = ({
    scope,
    onAddAccount,
    onAddGroup,
}: {
    scope: NestedAddScope;
    onAddAccount: (scope: NestedAddScope) => void;
    onAddGroup: (scope: NestedAddScope) => void;
}) => (
    <span
        className={classNames(
            styles.rowAddActions,
            viewTableStyles.showOnHover,
        )}
    >
        <Button variant="primary" size="md" onClick={() => onAddGroup(scope)}>
            Add group
        </Button>
        <Button variant="primary" size="md" onClick={() => onAddAccount(scope)}>
            Add account
        </Button>
    </span>
);

// Per-row ⋯ menu for a custom group: just Delete group (the Add group / Add
// account affordances are rendered alongside, see RowAddActions).
const GroupActionsMenu = ({
    groupId,
    callbacks,
}: {
    groupId: string;
    callbacks: NestedEditorCallbacks;
}) => (
    <Menu.More size="md" className={viewTableStyles.showOnHover}>
        <Menu.Item destructive onClick={() => callbacks.onDeleteGroup(groupId)}>
            Delete group
        </Menu.Item>
    </Menu.More>
);

// Right-pinned actions column. Every row exposes hover-revealed Add group / Add
// account affordances scoped to itself; account + custom group rows additionally
// expose a ⋯ menu, while structural section / built-in tier rows have none.
export const NestedActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, NestedEditorRow, unknown>) => {
        const item = row.original;
        const meta = table.options.meta as NestedEditorTableMeta | undefined;
        const callbacks = meta?.callbacks;
        const isWriting = useNestedEditorIsWriting();
        if (meta?.isEditingDisabled === true) {
            return <ViewTableCell />;
        }

        // Nothing may name a row that has not been named itself.
        const isPending =
            meta !== undefined && isRowPending(item, meta.pendingDraftIds);

        const resolveMenu = () => {
            if (!callbacks || !meta || isPending) {
                return null;
            }
            if (item.kind === "account" && item.account) {
                return (
                    <AccountActionsMenu
                        row={item}
                        account={item.account}
                        capabilities={meta.getRowCapabilities(item.account)}
                        getConstituentCapabilities={
                            meta.getConstituentCapabilities
                        }
                        callbacks={callbacks}
                        host={meta.host}
                        isWriting={isWriting}
                    />
                );
            }
            if (item.kind === "group" && item.group) {
                return (
                    <GroupActionsMenu
                        groupId={item.group.id}
                        callbacks={callbacks}
                    />
                );
            }
            return null;
        };

        const addScope = isPending
            ? null
            : resolveRowAddScope(item, meta?.displayedEntityIds);
        const addActions =
            addScope && callbacks ? (
                <RowAddActions
                    scope={addScope}
                    onAddAccount={callbacks.onAddAccount}
                    onAddGroup={callbacks.onAddGroup}
                />
            ) : null;
        const menu = resolveMenu();

        if (!addActions && !menu) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell captureClicks>
                <HStack gap={4} align="center" justify="end">
                    {addActions}
                    {menu}
                </HStack>
            </ViewTableCell>
        );
    },
);

NestedActionsCell.displayName = "NestedActionsCell";
